/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { ArrayExtensions, FinitePromiseQueue, FinitePromiseQueueError,
    getDimensionsForSelectedVariant, GetDimensionsForSelectedVariantInput,
    getProductAvailabilitiesForSelectedVariant, getScanResult, GetScanResultInput,
    getSelectedVariant, IProductInventoryInformation, IPromiseQueue, ProductAvailabilitiesForSelectedVariantInput,
    SelectedVariantInput } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { ProductDimensionFull } from '@msdyn365-commerce/commerce-entities';
import { getProductConfigure, IErrorState, IProductDimensionCallbacks, IProductDimensionConfigureResources,
    IProductDimensionConfigureViewProps } from '@msdyn365-commerce/components';
import { ProductDimension, ProductDimensionValue, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { computed,observable } from 'mobx';
import * as React from 'react';
import { QuickOrderDrawer, QuickOrderFormComponent } from './components';
import { IQuickOrderData } from './quick-order.data';
import { IQuickOrderProps, IQuickOrderResources } from './quick-order.props.autogenerated';

export interface IQuickOrderViewProps extends IQuickOrderProps<IQuickOrderData> {
    isB2B?: boolean;
    productText: string;
    isQuickOrderFormExpanded: boolean;
    contentProps: INodeProps ;
    drawer: INodeProps;
    quickOrderForm: INodeProps;
    quickOrderFormProductContent: INodeProps;
    quickOrderFormMsgContent: INodeProps;
    quickOrderProps: IModuleProps;
    form: React.ReactNode;
    configure?: IProductDimensionConfigureViewProps;
    isMobileView: boolean;
}

export interface IQuickOrderStateMessage {
    isOpen: boolean;
    messageGlyph: string;
    messageText: string;
    statusCssString: string;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface IQuickOrderState {
    isInitialState: boolean;
    isQuickOrderFormExpanded: boolean;
    isUpdatingDimension?: boolean;
    productAvailableQuantity: IProductInventoryInformation | undefined;
    productResponse: SimpleProduct | undefined;
    productDimensions: ProductDimensionFull[] | undefined;
    productText: string;
    quantity: number;
    stateMessage?: IQuickOrderStateMessage;
    productDimensionErrorState: IErrorState;
    isMobile: boolean;
}

/**
 *
 * QuickOrder component.
 * @extends {React.PureComponent<IQuickOrderProps<IQuickOrderData>, IQuickOrderState>}
 */
class QuickOrder extends React.PureComponent<IQuickOrderProps<IQuickOrderData>, IQuickOrderState> {
    private readonly productTextInput: React.RefObject<HTMLInputElement>;

    private readonly quantityInput: React.RefObject<HTMLInputElement>;

    private readonly waitTime: number = 500;

    private readonly quickOrderFormClass: string;

    private readonly quickOrderFormProductContent: string;

    private readonly quickOrderFormMsgContent: string;

    private readonly telemetryContent: ITelemetryContent;

    private readonly dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(2);

    private dimensions: { [id: number]: string } = {};

    private readonly cssClassNameConfigure: string = 'ms-quick-order__content-form-main';

    @observable
    private viewport: GridSize;

    // @ts-expect-error
    private cancellabelDebounce: debounce;

    private readonly quickOrderCallbacks: IProductDimensionCallbacks = {
        dimensionSelectedAsync: async (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            this.dimensions[selectedDimensionId] = selectedDimensionValueId;
            return this.dimensionUpdateQueue.enqueue(async () => {
                return this._updateDimensions();
            }).catch((error: any) => { // tslint:disable-line:no-any
                // Ignore discarded processes.
                if (error !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                    throw error;
                }
            });
        },
        getDropdownName: (dimensionType: number, resources: IProductDimensionConfigureResources): string => {
            return this._getDropdownName(dimensionType, resources);
        },
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension });
        }
    };

    public constructor(props: IQuickOrderProps<IQuickOrderData>) {
        super(props);
        this.productTextInput = React.createRef();
        this.quantityInput = React.createRef<HTMLInputElement>();
        this.viewport = props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';

        this.telemetryContent = getTelemetryObject(this.props.context?.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
        this.state = {
            productText: '',
            isInitialState: true,
            isQuickOrderFormExpanded: false,
            productAvailableQuantity: undefined,
            quantity: 1,
            productResponse: undefined,
            productDimensions: undefined,
            productDimensionErrorState: {
                configureErrors: {}
            },
            isMobile: this.isMobile
        };
        this._handleInputChange = this._handleInputChange.bind(this);
        this._handleQuantityChange = this._handleQuantityChange.bind(this);
        this.quickOrderFormClass = 'ms-quick-order__content-form';
        this.quickOrderFormProductContent = 'ms-quick-order__content-form-main';
        this.quickOrderFormMsgContent = 'ms-quick-order__content-form-msg';
    }

    @computed get isMobile(): boolean {
        return (this.viewport === 'xs' || this.viewport === 'sm');
    }

    public render(): JSX.Element | null {

        const { data } = this.props;
        if(!data.customerInformation?.result) {
            return null;
        }

        const { className = '', maxQuantity } = this.props.config;
        const customerInfo = this.props.data.customerInformation.result;
        const { quickOrderModuleHeading, quickOrderModuleDescription,
            quickOrderAddtobag, quickOrderProductQuantityInputLabel,
            quickOrderProductInputLabel, quickOrderProductDimensionTypeColor,
            quickOrderProductDimensionTypeConfiguration, quickOrderSelectDimensionFormatString,
            quickOrderProductDimensionTypeSize, quickOrderProductDimensionTypeStyle,
            quickOrderProductDimensionTypeAmount
        } = this.props.resources;

        const productConfigureResources: IProductDimensionConfigureResources = {
            selectDimensionFormatString: quickOrderSelectDimensionFormatString,
            productDimensionTypeColor: quickOrderProductDimensionTypeColor,
            productDimensionTypeConfiguration: quickOrderProductDimensionTypeConfiguration,
            productDimensionTypeSize: quickOrderProductDimensionTypeSize,
            productDimensionTypeStyle: quickOrderProductDimensionTypeStyle,
            productDimensionTypeAmount: quickOrderProductDimensionTypeAmount
        };

        const {
            isQuickOrderFormExpanded,
            productText,
            productDimensionErrorState,
            productResponse,
            productDimensions,
        } = this.state;

        const moduleClassName: string = 'ms-quick-order';

        const viewProps: IQuickOrderViewProps = {
            ...(this.props as IQuickOrderProps<IQuickOrderData>),

            quickOrderProps: { className: classnames('ms-quick-order', className), moduleProps: this.props },
            contentProps: { className: 'ms-quick-order__content' },
            isB2B: customerInfo && customerInfo.IsB2b,
            drawer: {
                tag: QuickOrderDrawer,
                className: `${moduleClassName}__drawer`,
                collapseProps: { isOpen: this.state.isQuickOrderFormExpanded },
                moduleHeadingText: quickOrderModuleHeading,
                moduleDescriptionText: quickOrderModuleDescription,
                requestContext: this.props.context.request,
                telemetryContent: this.telemetryContent
            },
            quickOrderForm: {
                className: `${this.quickOrderFormClass} ${isQuickOrderFormExpanded ? 'qo-show' : 'qo-hide'}`
            },
            quickOrderFormProductContent: {
                className: this.quickOrderFormProductContent
            },
            quickOrderFormMsgContent: {
                className: this.quickOrderFormMsgContent
            },
            productText,
            form: QuickOrderFormComponent({
                productTextInput: this.productTextInput,
                quantityTextInput: this.quantityInput,
                disableAddtoCart: productResponse ? false : true,
                ariaLabelAddtoCart: quickOrderAddtobag,
                ariaLabelProduct: quickOrderProductInputLabel,
                ariaLabelQuantity: quickOrderProductQuantityInputLabel,
                maxChars: 50,
                maxQuantity: maxQuantity,
                isQuickOrderFormExpanded,
                quickOrderState: this.state,
                handleInputChange: this._handleInputChange,
                handleAddToCartClick: this._addItemToCart,
                handleQuantityChange: this._handleQuantityChange
            }),
            configure: productResponse && productDimensions ? getProductConfigure(this.cssClassNameConfigure, this.props.context, this.props.friendlyName,
                                                                                  this.props.telemetry, quickOrderSelectDimensionFormatString,
                                                                                  productConfigureResources, this.quickOrderCallbacks, productResponse,
                                                                                  productDimensions, productDimensionErrorState) : undefined,
            isQuickOrderFormExpanded: this.state.isQuickOrderFormExpanded,
            isMobileView: this.state.isMobile
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly _handleInputChange = (query: string): void => {
        if (!query || query.length > 4) {
            this.setState({ productText: query, quantity: 0 });
            this.setState({ stateMessage: {
                messageText: '',
                statusCssString: '',
                messageGlyph: '',
                isOpen: false
            } });
        } else {
            return;
        }

        if (this.cancellabelDebounce) {
            this.cancellabelDebounce.cancel();
        }

        const {
            telemetry,
            context
        } = this.props;

        if (context) {
            if (query && query.length > 0) {
                if (query.length > 50) {
                    return;
                }

                // TODO: need set productAvailability in state.
                this.cancellabelDebounce = debounce(() => {
                    getScanResult(
                        new GetScanResultInput(context.actionContext.requestContext.apiSettings, query, context.actionContext.requestContext.apiSettings.channelId),
                        this.props.context.actionContext)
                        .then(results => {
                            this.setState({ productResponse: results?.Product, productDimensions: results?.ProductDimensionFull, isQuickOrderFormExpanded: true });
                        })
                        .catch(error => {
                            this.setState({ productText: '' });
                            telemetry.exception(error);
                            telemetry.debug('Unable to get scan results');
                        });
                },                                  this.waitTime);

                setTimeout(() => {
                    this.cancellabelDebounce();
                },         0);
                this.setState({ productText: query, isQuickOrderFormExpanded: true });
            } else {
                this.setState({ productText: '', productResponse: undefined, isQuickOrderFormExpanded: true });
            }
        }
    };

    private readonly _addItemToCart = async (): Promise<void> => {
        const { context, data, resources, telemetry } = this.props;
        const { cart } = data;
        const { quickOrderAddedToCartSuccessMessage, quickOrderAddedToCartFailureMessage } = resources;
        const { productAvailableQuantity, productResponse, quantity } = this.state;
        this.setState({ stateMessage: {
            messageText: '',
            statusCssString: '',
            messageGlyph: '',
            isOpen: false
        } });

        if (context && productResponse !== undefined && this._validateCart(productResponse, quantity)) {
            if (cart.result) {
                const isOrderQuantityLimitsFeatureEnabledInHq = this.props.data.featureState.result
                    ?.find(featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature')?.IsEnabled || false;
                const defaultOrderQuantityLimitsFeatureConfig = this.props.context.request.app.platform?.enableDefaultOrderQuantityLimits;
                const customerInfo = this.props.data.customerInformation.result;

                const isOrderQuantityLimitsFeatureEnabledInSiteSettings = defaultOrderQuantityLimitsFeatureConfig === 'all' ||
                    (customerInfo &&
                    ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                    (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b)));

                // Get product delivery mode info
                const deliveryOptions = await getDeliveryOptionsAsync(
                    { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                    [productResponse.RecordId]);
                const emailDeliveryModeCode = this.props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode;
                const defaultDeliveryOption = ArrayExtensions.hasElements(deliveryOptions) && ArrayExtensions.hasElements(deliveryOptions[0].DeliveryOptions) ? deliveryOptions[0].DeliveryOptions[0].Code : undefined;

                const isOrderQuantityLimitsFeatureEnabled = isOrderQuantityLimitsFeatureEnabledInHq && isOrderQuantityLimitsFeatureEnabledInSiteSettings;
                const addProductToCartInput = {
                    product: productResponse,
                    count: quantity,
                    enableStockCheck: context.app.config.enableStockCheck,
                    availableQuantity: productAvailableQuantity ? productAvailableQuantity.ProductAvailableQuantity.AvailableQuantity : 0,
                    additionalProperties: { orderQuantityLimitsFeatureIsEnabled: isOrderQuantityLimitsFeatureEnabled },
                    isAddEmailDeliveryItemToCart: defaultDeliveryOption !== undefined && defaultDeliveryOption === emailDeliveryModeCode,
                    deliveryMode: defaultDeliveryOption
                };

                cart.result.addProductToCart(addProductToCartInput)
                    .then(async result => {
                        if (result.status === 'SUCCESS') {
                            this.props.telemetry.debug('[quick-order] Cart Updated');

                            this.setState({ stateMessage: {
                                messageText: quickOrderAddedToCartSuccessMessage,
                                statusCssString: 'success',
                                messageGlyph: 'fas fa-check',
                                isOpen: true
                            } });
                        } else if (result.substatus === 'MAXQUANTITY') {
                            this.props.telemetry.debug('[quick-order] Unable to Update Cart Line from the quick order', quantity);
                            const maxQtyLimitReachedMessage = this._getMaxQtyMessage(productAvailableQuantity, quantity);

                            this.setState({ stateMessage: {
                                messageText: maxQtyLimitReachedMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            } });
                        } else if (result.substatus === 'QUANTITYLIMITS' && isOrderQuantityLimitsFeatureEnabled) {
                            const maxQtyLimitReachedMessage = result.errorDetails?.LocalizedMessage!;
                            this.props.telemetry.debug('[quick-order] Unable to Update Cart Line from the quick order', maxQtyLimitReachedMessage);

                            this.setState({ stateMessage: {
                                messageText: maxQtyLimitReachedMessage || quickOrderAddedToCartFailureMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            } });
                        } else {
                            this.props.telemetry.debug('[quick-order] Unable to Update Cart Line from the quick order');

                            this.setState({ stateMessage: {
                                messageText: quickOrderAddedToCartFailureMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            } });
                        }
                    })
                    .catch(error => {
                        telemetry.warning(error);
                        telemetry.debug('[quick-order] Error trying to Update Cart Line from the quick order');

                        this.setState({ stateMessage: {
                            messageText: quickOrderAddedToCartFailureMessage,
                            statusCssString: 'danger',
                            messageGlyph: 'fas fa-exclamation-triangle',
                            isOpen: true
                        } });
                    });
            }
        }
    };

    private _getMaxQtyMessage(productInventoryInfo: IProductInventoryInformation | undefined, curQuantity: number): string {
        const { context, resources } = this.props;
        const { quickOrderMaxQuantityLimitText } = resources;

        const maxQuantityForLineItem = context.app.config.maxQuantityForCartLineItem || 10;

        if (!productInventoryInfo || !productInventoryInfo.ProductAvailableQuantity.AvailableQuantity || !context.app.config.enableStockCheck) {
            return quickOrderMaxQuantityLimitText.replace('{maxQuantity}', maxQuantityForLineItem.toString()).replace('{curQuantity}', curQuantity.toString());
        }

        const minInventory = Math.min(maxQuantityForLineItem, productInventoryInfo.ProductAvailableQuantity.AvailableQuantity);

        return quickOrderMaxQuantityLimitText.replace('{maxQuantity}', minInventory.toString()).replace('{curQuantity}', curQuantity.toString());
    }

    private readonly _handleQuantityChange = (value: string): void => {
        const currentValue = Number.parseInt((value), 10);
        const inputElement = this.quantityInput && this.quantityInput.current && this.quantityInput.current instanceof HTMLInputElement && this.quantityInput.current;

        this.setState(
            { quantity: currentValue },
            () => {
                debounce(
                    () => {
                        if (!isNaN(this.state.quantity) && !(this.state.quantity < 0)) {
                            if (inputElement) {
                                inputElement.setAttribute('aria-valuenow', currentValue.toString());
                                inputElement.setAttribute('value', currentValue.toString());
                            }
                        }
                    },
                    200)();
            }
        );
    };

    private readonly _updateDimensionValue = (productDimensionFull: ProductDimensionFull, newValueId: string | undefined): ProductDimensionValue | undefined => {
        if (newValueId && productDimensionFull.DimensionValues) {
            return productDimensionFull.DimensionValues.find(dimension => dimension.RecordId === +newValueId);
        }

        return undefined;
    };

    private readonly _getDropdownName = (dimensionType: number, resources: IProductDimensionConfigureResources): string => {
        const isGiftCard = this.state.productResponse?.IsGiftCard;

        switch (dimensionType) {
            case 1: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case 2: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case 3: // ProductDimensionType.Size
                return resources.productDimensionTypeSize;
            case 4: // ProductDimensionType.Style
                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };

    // tslint:disable-next-line:max-func-body-length
    private readonly _updateDimensions = async (): Promise<void> => {
        const {
            context: {
                actionContext,
                request: {
                    apiSettings: {
                        channelId
                    }
                }
            }
        } = this.props;
        const { productDimensions, productResponse } = this.state;
        if (!productResponse || !productDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this.dimensions };
        this.setState({ isUpdatingDimension: true });

        // Step 1: Update state to indicate which dimensions are selected
        if (this.state.productDimensionErrorState.otherError) {
            const clearOtherErrorState = { ...this.state.productDimensionErrorState };
            clearOtherErrorState.otherError = undefined;
            this.setState({ productDimensionErrorState: clearOtherErrorState });
        }

        // Step 2: Clear any errors indicating the dimension wasn't selected
        for (const key of Object.keys(dimensionsToUpdate)) {
            if (this.state.productDimensionErrorState.configureErrors[key]) {
                const errorState = { ...this.state.productDimensionErrorState };
                errorState.configureErrors[key] = undefined;

                this.setState({ productDimensionErrorState: errorState });
            }
        }

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions.map(dimension => {
            return {
                DimensionTypeValue: dimension.DimensionTypeValue,
                DimensionValue: this._updateDimensionValue(dimension, dimensionsToUpdate[dimension.DimensionTypeValue]) || dimension.DimensionValue,
                ExtensionProperties: dimension.ExtensionProperties
            };
        }).filter(dimension => {
            return dimension && dimension.DimensionValue;
        });

        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        const selectedProduct = new Promise<SimpleProduct | null>(async (resolve, reject) => {
            const newProduct = (await getSelectedVariant(
                new SelectedVariantInput(
                    productResponse.MasterProductId ? productResponse.MasterProductId : productResponse.RecordId,
                    channelId,
                    mappedDimensions
                ),
                actionContext
            ));
            if (newProduct) {
                await getDimensionsForSelectedVariant(
                    new GetDimensionsForSelectedVariantInput(
                        newProduct.MasterProductId ? newProduct.MasterProductId : newProduct.RecordId,
                        channelId,
                        mappedDimensions
                    ),
                    actionContext
                );
            }

            resolve(newProduct);
        });

        const variantProduct = await selectedProduct;
        this.setState({ productResponse: variantProduct !== null ?  variantProduct: undefined});
        if (variantProduct) {
            // Step 5. Use these dimensions hydrate the inventory. Wrap this in a promise
            // so that places like add to cart can await it
            const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
                new ProductAvailabilitiesForSelectedVariantInput(
                    variantProduct.RecordId,
                    channelId
                ),
                actionContext
            );

            if (newAvailableQuantity && newAvailableQuantity.length) {
                this.setState({ productAvailableQuantity: newAvailableQuantity[0] });
            } else {
                this.setState({ productAvailableQuantity: undefined });
            }
        }
    };

    private readonly _getDimensionErrorString = (dimensionTypeValue: number, resources: IQuickOrderResources, isGiftCard?: Boolean): string => {
        switch (dimensionTypeValue) {
            case 1: // ProductDimensionType.Color
                return resources.quickOrderproductDimensionTypeColorErrorMessage;
            case 2: // ProductDimensionType.Configuration
                return resources.quickOrderproductDimensionTypeConfigurationErrorMessage;
            case 3: // ProductDimensionType.Size
                return resources.quickOrderproductDimensionTypeSizeErrorMessage;
            case 4: // ProductDimensionType.Style
                return isGiftCard ? resources.quickOrderproductDimensionTypeAmountErrorMessage : resources.quickOrderproductDimensionTypeStyleErrorMessage;
            default:
                return '';
        }
    }

    private readonly _validateCart = (productResponse: SimpleProduct, quantity: number): boolean => {
        const missingDimensions = productResponse?.Dimensions && productResponse.Dimensions.filter((dimension: ProductDimension) => !(dimension.DimensionValue && dimension.DimensionValue.Value));
        if (missingDimensions && missingDimensions.length > 0) {
           const userMsg =  this._getConfigureErrors(missingDimensions, false);
           if (this.state.productDimensionErrorState.configureErrors) {
            const errorState = { ...this.state.productDimensionErrorState };
            errorState.configureErrors = userMsg;

            this.setState({ productDimensionErrorState: errorState });
            return false;
            }
        }

        if (quantity <= 0) {
            const resources = this.props.resources;
            this.setState({ stateMessage: {
                messageText: resources.quickOrderWrongQuantityErrorMsg,
                statusCssString: 'danger',
                messageGlyph: 'fas fa-exclamation-triangle',
                isOpen: true
            } });
            return false;
        }
        return true;
    }

    private _getConfigureErrors(result: ProductDimension[] | undefined, isGiftCard?: Boolean): { [configureId: string]: string | undefined } {
        if (!result) {
            return {};
        }
        const dimensions: { [configureId: string]: string | undefined } = {};
        const resources = this.props.resources;
        result.forEach(dimension => {
            dimensions[dimension.DimensionTypeValue] = this._getDimensionErrorString(dimension.DimensionTypeValue, resources, isGiftCard);
        });
        return dimensions;
    }
}

export default QuickOrder;
